import { Reveal } from "../lib/motion";
import { useLang } from "../lib/i18n";
import { Kicker, SectionTitle, Watermark } from "./Shared";
import { CywizText } from "./CywizMark";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

/**
 * FAQ d'objections. Deux effets :
 * 1) commercial — on désamorce les freins avant le rendez-vous ;
 * 2) SEO — les questions/réponses sont éligibles aux résultats enrichis.
 * Le JSON-LD FAQPage est injecté ci-dessous à partir des mêmes données.
 */
export const Faq = ({ items, kicker, title, desc, watermark = "05" }) => {
  const { t } = useLang();
  const f = t.faq;
  const list = items || f.items;

  // Les données structurées n'acceptent pas de mise en forme : le jeton
  // {cywiz} y est remplacé par le nom en clair, comme dans la balise <title>.
  const plain = (s) => s.replace(/\{cywiz\}/g, "Cywiz");

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: list.map((item) => ({
      "@type": "Question",
      name: plain(item.q),
      acceptedAnswer: { "@type": "Answer", text: plain(item.a) },
    })),
  };

  return (
    <section id="faq" data-testid="faq-section" className="relative pt-40 md:pt-56 pb-20 md:pb-28 overflow-hidden">
      <Watermark n={watermark} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <div className="relative z-10 max-w-site mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-5 min-w-0">
            <Kicker>{kicker || f.kicker}</Kicker>
            <SectionTitle
              lead={(title || f.title)[0]}
              accent={(title || f.title)[1]}
              stacked
            />
            {desc && <p className="mt-6 text-warm font-light leading-relaxed">{desc}</p>}
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-6 lg:col-start-7">
            <Accordion type="single" collapsible className="w-full">
              {list.map((item, i) => (
                <AccordionItem
                  key={item.q}
                  value={`q-${i}`}
                  className="border-b border-copper-light/35"
                >
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="group/faq text-left font-display text-base md:text-lg font-normal text-warm hover:text-copper data-[state=open]:text-copper hover:no-underline py-6 transition-colors duration-300 [&>svg]:text-dim group-hover/faq:[&>svg]:text-copper data-[state=open]:[&>svg]:text-copper"
                  >
                    <CywizText>{item.q}</CywizText>
                  </AccordionTrigger>
                  <AccordionContent className="pb-7 pr-6 text-sm md:text-base text-warm font-light leading-relaxed">
                    <CywizText>{item.a}</CywizText>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
